import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Paper, Button } from '@c2fo/liquidity';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';
import { CloneDivisionsModal } from '../CloneDivisionsModal';
import { StatusBadge } from '../StatusBadge';
import {
  DataTable,
  TableHeader,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableSubHeaderCell,
  TableCell,
} from '../DataTable';
import type { Division, User } from '../../types';
import { AddDivisionForm } from './AddDivisionForm';
import { AddDivisionsConfirmationModal } from './AddDivisionsConfirmationModal';
import type { Division as DivisionSearchResult } from '../../../../components/DivisionSearch';

interface DivisionsTableProps {
  divisions: Division[];
  userId: string;
  userName: string;
  userEmail: string;
  user: User;
  onDivisionsChange?: () => void;
}

export const DivisionsTable: React.FC<DivisionsTableProps> = ({
  divisions,
  userId,
  userName,
  userEmail,
  user,
  onDivisionsChange,
}) => {
  const [addDivisionFormVisible, setAddDivisionFormVisible] = useState(false);
  const [cloneDivisionsModalOpen, setCloneDivisionsModalOpen] = useState(false);
  const [selectedDivisionsForConfirm, setSelectedDivisionsForConfirm] = useState<DivisionSearchResult[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isUserForgotten = !!user?.forgottenOn;

  const handleAddDivisionFormCancel = () => {
    setAddDivisionFormVisible(false);
  };

  const handleAddDivisionFormConfirm = (selectedDivisions: DivisionSearchResult[]) => {
    setSelectedDivisionsForConfirm(selectedDivisions);
    setShowConfirmModal(true);
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
    setSelectedDivisionsForConfirm([]);
  };

  const handleAddDivisionsSuccess = () => {
    setShowConfirmModal(false);
    setAddDivisionFormVisible(false);
    setSelectedDivisionsForConfirm([]);
    if (onDivisionsChange) {
      onDivisionsChange();
    }
  };

  return (
    <Paper elevation={1} square className="p-6 mb-8">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-sm font-semibold m-0" aria-label={`${divisions.length} associated divisions (suppliers)`}>
          {divisions.length} Associated Divisions (Suppliers)
        </h3>
      </div>
      <div className="flex gap-2 mb-4">
        {!addDivisionFormVisible ? (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setAddDivisionFormVisible(true)}
              disabled={isUserForgotten}
              aria-label="Add division to user"
            >
              Add Division
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCloneDivisionsModalOpen(true)}
              disabled={isUserForgotten}
              aria-label="Clone divisions from another user"
            >
              Clone Divisions
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAddDivisionFormCancel}
            aria-label="Cancel adding division"
          >
            Cancel
          </Button>
        )}
      </div>

      {addDivisionFormVisible && (
        <AddDivisionForm
          userId={userId}
          userName={userName}
          disabled={isUserForgotten}
          onCancel={handleAddDivisionFormCancel}
          onConfirm={handleAddDivisionFormConfirm}
        />
      )}

      {divisions.length > 0 && (
        <DataTable aria-label="Associated divisions table">
          <TableHeader>
            <tr>
              <TableHeaderCell rowSpan={2} className="w-10">
                #
              </TableHeaderCell>
              <TableHeaderCell rowSpan={2} className="min-w-[150px] text-left">
                Name
              </TableHeaderCell>
              <TableHeaderCell colSpan={3}>Invoice Amounts</TableHeaderCell>
              <TableHeaderCell colSpan={2}>Offer Settings</TableHeaderCell>
              <TableHeaderCell colSpan={4}>Activity</TableHeaderCell>
              <TableHeaderCell colSpan={3}>Buyer</TableHeaderCell>
              <TableHeaderCell rowSpan={2} className="min-w-[120px] text-left">
                SRM
              </TableHeaderCell>
            </tr>
            <tr>
              <TableSubHeaderCell>Eligible</TableSubHeaderCell>
              <TableSubHeaderCell>DPE</TableSubHeaderCell>
              <TableSubHeaderCell>Clearing</TableSubHeaderCell>
              <TableSubHeaderCell>Status</TableSubHeaderCell>
              <TableSubHeaderCell>Type</TableSubHeaderCell>
              <TableSubHeaderCell>Offer</TableSubHeaderCell>
              <TableSubHeaderCell>Age</TableSubHeaderCell>
              <TableSubHeaderCell>SignedUp</TableSubHeaderCell>
              <TableSubHeaderCell>Cleared</TableSubHeaderCell>
              <TableSubHeaderCell>Market</TableSubHeaderCell>
              <TableSubHeaderCell>DivisionID</TableSubHeaderCell>
              <TableSubHeaderCell>Buyer/SupplierID</TableSubHeaderCell>
            </tr>
          </TableHeader>
          <TableBody>
            {divisions.map((division, index) => (
              <TableRow key={`${division.uuid}-${index}`} index={index}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Link
                    to={`/divisions/${division.makerOrganizationUuid}/${division.takerId || division.id}`}
                    className="text-blue-600 no-underline hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    aria-label={`View division ${division.name}`}
                  >
                    {division.name}
                  </Link>
                </TableCell>
                <TableCell>${division.eligibleInvoiceAmount?.toLocaleString() ?? '-'}</TableCell>
                <TableCell>{division.eligibleDpeWeightedAvg ?? '-'}</TableCell>
                <TableCell>${division.acceptedInvoiceAmount?.toLocaleString() ?? '-'}</TableCell>
                <TableCell>
                  <StatusBadge
                    type={division.takerConfigIsEnabled ? 'primary' : 'error'}
                    label={division.takerConfigIsEnabled ? 'OFFER ON' : 'OFFER OFF'}
                    aria-label={`Offer status: ${division.takerConfigIsEnabled ? 'ON' : 'OFF'}`}
                  />
                </TableCell>
                <TableCell>
                  <StatusBadge
                    type="info"
                    label={division.takerConfigIsDiscountBidding ? 'Disc' : 'APR'}
                    aria-label={`Offer type: ${division.takerConfigIsDiscountBidding ? 'Discount' : 'APR'}`}
                  />
                </TableCell>
                <TableCell>{division.normalizedBid}%</TableCell>
                <TableCell>-</TableCell>
                <TableCell>
                  <StatusBadge
                    type={division.signedUpCount > 0 ? 'primary' : 'default'}
                    label={division.signedUpCount > 0 ? 'Yes' : 'No'}
                    aria-label={`Signed up: ${division.signedUpCount > 0 ? 'Yes' : 'No'}`}
                  />
                </TableCell>
                <TableCell>{division.lastClearDate ? formatDateWithTimezone(division.lastClearDate) : '-'}</TableCell>
                <TableCell>
                  {division.marketUuid ? (
                    <Link
                      to={`/markets/${division.marketUuid}`}
                      className="text-blue-600 no-underline hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                      aria-label={`View market ${division.makerDivisionName}`}
                    >
                      {division.makerDivisionName} {division.marketType}
                    </Link>
                  ) : (
                    <span>
                      {division.makerDivisionName} {division.marketType}
                    </span>
                  )}
                </TableCell>
                <TableCell>{division.id}</TableCell>
                <TableCell>{division.buyerSupplierId}</TableCell>
                <TableCell>
                  {division.srmFirstName} {division.srmLastName}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </DataTable>
      )}

      <CloneDivisionsModal
        open={cloneDivisionsModalOpen}
        onClose={() => setCloneDivisionsModalOpen(false)}
        targetUserId={userId}
        targetUserEmail={userEmail}
        targetUserName={userName}
        onDivisionsChange={onDivisionsChange}
      />

      <AddDivisionsConfirmationModal
        open={showConfirmModal}
        onClose={handleConfirmModalClose}
        userId={userId}
        userName={userName}
        selectedDivisions={selectedDivisionsForConfirm}
        onSuccess={handleAddDivisionsSuccess}
      />
    </Paper>
  );
};
